/**
 * Nav — terrain-aware shared flow-field pathfinding (sim-side, render-free).
 *
 * Scale contract: with 20,000+ soldiers the sim thinks in *formations*.
 * A* per soldier is never an option, so pathing works in two layers:
 *
 *  1. **Formation anchors** are the only pathing agents. A Dijkstra pass
 *     over a shared navigation grid builds one *flow field* per
 *     (team, destination-goal); every formation marching to that goal
 *     reuses the same field. Fields are rebuilt on a cadence
 *     (REPATH_SECONDS), not per frame and not per formation.
 *  2. **Soldiers** keep their formation-slot offsets and steer toward
 *     slot positions around the moving anchor (see render/units.ts) —
 *     local steering only, no pathfinding.
 *
 * Terrain awareness: traversal cost comes from the heightfield
 * (slope penalty; extreme slopes blocked for future hand-authored maps)
 * and from forest density (timber-node splats, same sigma as the fog of
 * war system in vision.ts). There is no water in the prototype terrain
 * or UE data, but an optional `isBlockedAt` hook marks impassable cells.
 *
 * Prototype tuning constants are grouped in NAV below and marked as
 * such; they must not become competing canonical data. Movement speed
 * itself stays data-driven (`move_speed` on the unit def).
 */
export interface NavTuning {
  /** Playable world extent, world units (matches VisionSystem default). */
  worldSize: number;
  /** Grid cell size, world units. 4u -> 100x100 cells over 400u. */
  cellSize: number;
  /** Flow fields are rebuilt at most this often per (team, goal), seconds. */
  repathSeconds: number;
  /** Max cached fields; oldest evicted (insertion order). */
  maxFields: number;
  /** Slope (rise/run) above which a cell is impassable. */
  slopeBlock: number;
  /** Cost multiplier per unit slope: cost = 1 + slopeCostK * slope. */
  slopeCostK: number;
  /** Forest cost: cost = 1 + forestCostK * density (density 0..1). */
  forestCostK: number;
  /** Gaussian sigma of a timber splat (matches vision.ts FOREST_SIGMA). */
  forestSigma: number;
  /** Splat cutoff radius (3 sigma, matches vision.ts). */
  forestSplatRadius: number;
  /** Field distance below which the march counts as arrived. */
  arriveRadius: number;
  /** Spiral search radius (cells) for a passable goal near a blocked one. */
  goalSearchRadius: number;
  /** Max separation push applied to a march anchor per call, world units. */
  maxSeparationPush: number;
}

/**
 * PROTOTYPE TUNING — not canonical data. The UE data model has no
 * slope/forest movement fields, so these live here until the UE nav
 * design lands. Speed stays data-driven via `move_speed`.
 */
export const NAV: NavTuning = {
  worldSize: 400,
  cellSize: 4,
  repathSeconds: 2.0,
  maxFields: 24,
  slopeBlock: 0.6,
  slopeCostK: 4.0,
  forestCostK: 2.0,
  forestSigma: 5,
  forestSplatRadius: 15,
  arriveRadius: 6,
  goalSearchRadius: 24,
  maxSeparationPush: 3,
};

/** One computed flow field: Dijkstra distances + per-cell travel direction. */
export interface FlowField {
  /** Quantized goal cell index this field was built for. */
  goalCell: number;
  /** Goal world position (clamped, possibly snapped to passable). */
  goalX: number;
  goalZ: number;
  /** Dijkstra distance to goal per cell; Infinity = unreachable. */
  dist: Float32Array;
  /** Unit travel direction per cell (toward the goal), x/z components. */
  dirX: Float32Array;
  dirZ: Float32Array;
  /** Sim time of construction (drives the repath cadence). */
  builtAt: number;
}

import type { RoadRect } from "./roads";

export interface NavOpts {
  heightAt: (x: number, z: number) => number;
  /** Optional extra impassable test (water, cliffs). Null = none. */
  isBlockedAt?: ((x: number, z: number) => boolean) | null;
  tuning?: Partial<NavTuning>;
}

/** Minimal binary heap for Dijkstra (dist, cell) pairs. */
class Heap {
  private d: number[] = [];
  private c: number[] = [];
  get size(): number {
    return this.d.length;
  }
  push(dist: number, cell: number): void {
    const d = this.d;
    const c = this.c;
    d.push(dist);
    c.push(cell);
    let i = d.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      // Deterministic tie-break: lower cell index wins on equal dist.
      if (d[p] < d[i] || (d[p] === d[i] && c[p] <= c[i])) break;
      [d[p], d[i]] = [d[i], d[p]];
      [c[p], c[i]] = [c[i], c[p]];
      i = p;
    }
  }
  pop(): [number, number] {
    const d = this.d;
    const c = this.c;
    const td = d[0];
    const tc = c[0];
    const ld = d.pop()!;
    const lc = c.pop()!;
    if (d.length > 0) {
      d[0] = ld;
      c[0] = lc;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (
          l < d.length &&
          (d[l] < d[m] || (d[l] === d[m] && c[l] < c[m]))
        )
          m = l;
        if (
          r < d.length &&
          (d[r] < d[m] || (d[r] === d[m] && c[r] < c[m]))
        )
          m = r;
        if (m === i) break;
        [d[m], d[i]] = [d[i], d[m]];
        [c[m], c[i]] = [c[i], c[m]];
        i = m;
      }
    }
    return [td, tc];
  }
}

const DIRS = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

export class NavSystem {
  readonly t: NavTuning;
  readonly cells: number;
  readonly half: number;
  private readonly heightAt: (x: number, z: number) => number;
  private readonly isBlockedAt: ((x: number, z: number) => boolean) | null;
  /** Traversal cost multiplier per cell; Infinity = impassable. */
  private cost: Float32Array;
  private splats: Array<{ x: number; z: number }> = [];
  /** Road rects (sim/roads.ts contract); lower traversal cost on roads. */
  private roadRects: RoadRect[] = [];
  private readonly fields = new Map<string, FlowField>();

  constructor(opts: NavOpts) {
    this.t = { ...NAV, ...(opts.tuning ?? {}) };
    this.cells = Math.round(this.t.worldSize / this.t.cellSize);
    this.half = this.t.worldSize / 2;
    this.heightAt = opts.heightAt;
    this.isBlockedAt = opts.isBlockedAt ?? null;
    this.cost = new Float32Array(this.cells * this.cells);
    this.bake();
  }

  /** Forest splat centres (live timber nodes); rebakes costs + drops fields. */
  setForestSplats(splats: Array<{ x: number; z: number }>): void {
    this.splats = splats;
    this.bake();
    this.fields.clear();
  }

  /**
   * Road rects (see sim/roads.ts for the data contract). Roads divide
   * traversal cost by their move bonus, so Dijkstra *prefers* roads when
   * a route exists. Rebakes costs and drops cached fields.
   */
  setRoads(rects: RoadRect[]): void {
    this.roadRects = rects;
    this.bake();
    this.fields.clear();
  }

  /** Number of cached flow fields (diagnostics / tests). */
  get fieldCount(): number {
    return this.fields.size;
  }

  cellAt(x: number, z: number): number {
    const n = this.cells;
    const ix = Math.max(
      0,
      Math.min(n - 1, Math.floor(((x + this.half) / this.t.worldSize) * n)),
    );
    const iz = Math.max(
      0,
      Math.min(n - 1, Math.floor(((z + this.half) / this.t.worldSize) * n)),
    );
    return iz * n + ix;
  }

  cellCenter(cell: number): { x: number; z: number } {
    const n = this.cells;
    const ix = cell % n;
    const iz = Math.floor(cell / n);
    return {
      x: -this.half + ((ix + 0.5) / n) * this.t.worldSize,
      z: -this.half + ((iz + 0.5) / n) * this.t.worldSize,
    };
  }

  isBlockedCell(cell: number): boolean {
    return !Number.isFinite(this.cost[cell]);
  }

  private bake(): void {
    const n = this.cells;
    const cs = this.t.cellSize;
    const sigma = this.t.forestSigma;
    const cutoff = this.t.forestSplatRadius;
    const cutoff2 = cutoff * cutoff;
    const denom = 2 * sigma * sigma;
    for (let iz = 0; iz < n; iz++) {
      for (let ix = 0; ix < n; ix++) {
        const cell = iz * n + ix;
        const x = -this.half + ((ix + 0.5) / n) * this.t.worldSize;
        const z = -this.half + ((iz + 0.5) / n) * this.t.worldSize;
        let c = 1;
        if (this.isBlockedAt?.(x, z)) {
          c = Infinity;
        } else {
          const e = cs * 0.5;
          const sx = (this.heightAt(x + e, z) - this.heightAt(x - e, z)) / (2 * e);
          const sz = (this.heightAt(x, z + e) - this.heightAt(x, z - e)) / (2 * e);
          const slope = Math.hypot(sx, sz);
          if (slope > this.t.slopeBlock) {
            c = Infinity;
          } else {
            c = 1 + this.t.slopeCostK * slope;
            if (this.splats.length > 0) {
              let density = 0;
              for (const s of this.splats) {
                const dx = x - s.x;
                const dz = z - s.z;
                const d2 = dx * dx + dz * dz;
                if (d2 < cutoff2) density += Math.exp(-d2 / denom);
              }
              if (density > 0) c *= 1 + this.t.forestCostK * Math.min(density, 1);
            }
            // Road preference: divide cost by the strongest road bonus
            // covering the cell, so routes snap to roads when they help.
            if (this.roadRects.length > 0) {
              let bonus = 1;
              for (const r of this.roadRects) {
                if (
                  Math.abs(x - r.x) <= r.hw &&
                  Math.abs(z - r.z) <= r.hd &&
                  r.moveBonus > bonus
                ) {
                  bonus = r.moveBonus;
                }
              }
              if (bonus > 1) c /= bonus;
            }
          }
        }
        this.cost[cell] = c;
      }
    }
  }

  /**
   * Get (or build) the shared flow field for `team` marching to
   * (`destX`, `destZ`). Cached per goal cell; rebuilt when older than
   * repathSeconds. Returns null only if no passable goal exists nearby.
   */
  requestField(
    team: number,
    destX: number,
    destZ: number,
    simNow: number,
  ): FlowField | null {
    const half = this.half - 1;
    const gx = Math.max(-half, Math.min(half, destX));
    const gz = Math.max(-half, Math.min(half, destZ));
    let goal = this.cellAt(gx, gz);
    if (this.isBlockedCell(goal)) {
      const found = this.nearestPassable(goal);
      if (found < 0) return null;
      goal = found;
    }
    const key = team + ":" + goal;
    const cached = this.fields.get(key);
    if (cached && simNow - cached.builtAt < this.t.repathSeconds) {
      return cached;
    }
    const center = this.cellCenter(goal);
    const field = this.buildField(goal, center.x, center.z, simNow);
    this.fields.set(key, field);
    while (this.fields.size > this.t.maxFields) {
      const oldest = this.fields.keys().next();
      if (oldest.done) break;
      this.fields.delete(oldest.value);
    }
    return field;
  }

  /** Spiral search for the nearest passable cell; -1 when none in range. */
  private nearestPassable(blockedCell: number): number {
    const n = this.cells;
    const ix0 = blockedCell % n;
    const iz0 = Math.floor(blockedCell / n);
    const R = this.t.goalSearchRadius;
    for (let r = 1; r <= R; r++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
          const ix = ix0 + dx;
          const iz = iz0 + dz;
          if (ix < 0 || iz < 0 || ix >= n || iz >= n) continue;
          const cell = iz * n + ix;
          if (!this.isBlockedCell(cell)) return cell;
        }
      }
    }
    return -1;
  }

  private buildField(
    goal: number,
    goalX: number,
    goalZ: number,
    simNow: number,
  ): FlowField {
    const n = this.cells;
    const total = n * n;
    const dist = new Float32Array(total).fill(Infinity);
    const dirX = new Float32Array(total);
    const dirZ = new Float32Array(total);
    const heap = new Heap();
    dist[goal] = 0;
    heap.push(0, goal);
    const cost = this.cost;
    while (heap.size > 0) {
      const [d, cell] = heap.pop();
      if (d > dist[cell]) continue;
      const ix = cell % n;
      const iz = Math.floor(cell / n);
      for (const [ox, oz, stepLen] of DIRS) {
        const nx = ix + ox;
        const nz = iz + oz;
        if (nx < 0 || nz < 0 || nx >= n || nz >= n) continue;
        const nc = nz * n + nx;
        const cc = cost[nc];
        if (!Number.isFinite(cc)) continue;
        // Entering-cost: average of the two cells' multipliers.
        const nd = d + ((cost[cell] + cc) * 0.5) * stepLen * this.t.cellSize;
        if (nd < dist[nc]) {
          dist[nc] = nd;
          heap.push(nd, nc);
        }
      }
    }
    // Flow direction: step toward the neighbor with the lowest distance.
    for (let cell = 0; cell < total; cell++) {
      if (!Number.isFinite(dist[cell]) || cell === goal) continue;
      const ix = cell % n;
      const iz = Math.floor(cell / n);
      let best = dist[cell];
      let bx = 0;
      let bz = 0;
      for (const [ox, oz] of DIRS) {
        const nx = ix + ox;
        const nz = iz + oz;
        if (nx < 0 || nz < 0 || nx >= n || nz >= n) continue;
        const nc = nz * n + nx;
        if (dist[nc] < best) {
          best = dist[nc];
          bx = ox;
          bz = oz;
        }
      }
      const len = Math.hypot(bx, bz);
      if (len > 0) {
        dirX[cell] = bx / len;
        dirZ[cell] = bz / len;
      }
    }
    return { goalCell: goal, goalX, goalZ, dist, dirX, dirZ, builtAt: simNow };
  }

  /**
   * Sample the travel direction at a world position (bilinear over the
   * direction grid). Returns dir (0,0) + dist Infinity when unreachable;
   * callers fall back to straight-line steering in that case.
   */
  sample(
    field: FlowField,
    x: number,
    z: number,
  ): { dx: number; dz: number; dist: number } {
    const n = this.cells;
    const fx = ((x + this.half) / this.t.worldSize) * n - 0.5;
    const fz = ((z + this.half) / this.t.worldSize) * n - 0.5;
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    let dx = 0;
    let dz = 0;
    for (let oz = 0; oz <= 1; oz++) {
      for (let ox = 0; ox <= 1; ox++) {
        const cx = Math.max(0, Math.min(n - 1, ix + ox));
        const cz = Math.max(0, Math.min(n - 1, iz + oz));
        const w = (ox === 0 ? 1 - tx : tx) * (oz === 0 ? 1 - tz : tz);
        const cell = cz * n + cx;
        dx += field.dirX[cell] * w;
        dz += field.dirZ[cell] * w;
      }
    }
    const len = Math.hypot(dx, dz);
    if (len > 1e-6) {
      dx /= len;
      dz /= len;
    } else {
      // Degenerate (goal cell, or averaging opposing flows): use the
      // nearest cell's exact direction.
      const cell = this.cellAt(x, z);
      dx = field.dirX[cell];
      dz = field.dirZ[cell];
    }
    return { dx, dz, dist: field.dist[this.cellAt(x, z)] };
  }

  /**
   * Advance one march anchor along the field. Pure function of
   * (field, position, speed, dt) — the formation-level pathing step.
   * `arrived` when the field distance drops below arriveRadius.
   */
  advance(
    field: FlowField,
    x: number,
    z: number,
    speed: number,
    dt: number,
  ): { x: number; z: number; dx: number; dz: number; arrived: boolean } {
    const s = this.sample(field, x, z);
    let dx = s.dx;
    let dz = s.dz;
    if (Math.hypot(dx, dz) < 1e-6 || !Number.isFinite(s.dist)) {
      // Unreachable cell (shouldn't happen mid-march; separation may have
      // pushed us there): fall back to straight-line steering to the goal.
      const gx = field.goalX - x;
      const gz = field.goalZ - z;
      const len = Math.hypot(gx, gz);
      if (len > 1e-6) {
        dx = gx / len;
        dz = gz / len;
      }
    }
    const step = speed * dt;
    const half = this.half - 1;
    const nx = Math.max(-half, Math.min(half, x + dx * step));
    const nz = Math.max(-half, Math.min(half, z + dz * step));
    const arrived =
      s.dist < this.t.arriveRadius ||
      Math.hypot(field.goalX - nx, field.goalZ - nz) < this.t.arriveRadius;
    return { x: nx, z: nz, dx, dz, arrived };
  }

  /** Field distance-to-goal at a world position (Infinity = unreachable). */
  distanceTo(field: FlowField, x: number, z: number): number {
    return field.dist[this.cellAt(x, z)];
  }
}

/**
 * Formation-vs-formation separation push for one march anchor.
 * `neighbors` are nearby formation anchors gathered via a spatial hash
 * (see sim/spatial.ts) — never a pairwise all-vs-all scan. Returns the
 * offset to apply, capped at maxSeparationPush so it can never explode.
 * Deterministic: no RNG, order-independent accumulation.
 */
export function separationPush(
  ax: number,
  az: number,
  selfRadius: number,
  neighbors: Array<{ x: number; z: number; radius: number }>,
  maxPush: number = NAV.maxSeparationPush,
): { x: number; z: number } {
  let px = 0;
  let pz = 0;
  for (const nb of neighbors) {
    const dx = ax - nb.x;
    const dz = az - nb.z;
    const d = Math.hypot(dx, dz);
    const want = (selfRadius + nb.radius) * 0.75;
    if (d < want && d > 1e-3) {
      const push = ((want - d) / want) * (want - d) * 0.5;
      px += (dx / d) * push;
      pz += (dz / d) * push;
    }
  }
  const len = Math.hypot(px, pz);
  if (len > maxPush && len > 1e-9) {
    px = (px / len) * maxPush;
    pz = (pz / len) * maxPush;
  }
  return { x: px, z: pz };
}
